import React, { useState, useEffect, useContext } from 'react'
import SecondChanceModal from 'components/App/SecondChanceModal'
import ModalContext from 'contexts/ModalContext'
import { useHistory } from 'react-router'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOffer from 'hooks/Offers/useOffer'
import useQueryParams from 'hooks/useQueryParams'
export enum PageType {
  Offer = 'offer',
  Search = 'search'
}

const isFromPaidAd = (queries: URLSearchParams) => {
  return queries.get('utm_medium') === 'cpc' && queries.get('utm_source') === 'google'
}

export type SecondChanceModalVariant = 'with_promo' | 'without_promo' | 'control' | undefined;

interface Props {
  offerId?: string;
  pageType?: PageType;
}

const useSecondChanceModal = ({ offerId, pageType } : Props) => {
  const isOfferPage = pageType === 'offer'
  const isSearchPage = pageType === 'search'
  const [shouldRunExperiment, setShouldRunExperiment] = useState(false)
  useEffect(() => {
    const handleInteraction = () => {
      setShouldRunExperiment(true)
    }

    window.addEventListener('click', handleInteraction)
    window.addEventListener('keydown', handleInteraction)

    return () => {
      window.removeEventListener('click', handleInteraction)
      window.removeEventListener('keydown', handleInteraction)
    }
  }, [])
  // As popstate won't be fired without interaction, only run the experiment after the popstate is fired.
  const offerPageSecondChanceModalVariant = useOptimizelyExperiment(OptimizelyExperiments.offerPageSecondChanceModal, isOfferPage && shouldRunExperiment) as SecondChanceModalVariant
  const searchPageSecondChanceModalVariant = useOptimizelyExperiment(OptimizelyExperiments.searchPageSecondChanceModal, isSearchPage && shouldRunExperiment) as SecondChanceModalVariant

  const [seedOffer] = useOffer(offerId)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const showModal = useContext(ModalContext)
  const history = useHistory()

  // Should run experiment when there is any interaction.
  useEffect(() => {
    const handleInteraction = () => {
      setShouldRunExperiment(true)
    }

    window.addEventListener('click', handleInteraction)
    window.addEventListener('keydown', handleInteraction)

    return () => {
      window.removeEventListener('click', handleInteraction)
      window.removeEventListener('keydown', handleInteraction)
    }
  }, [])

  const queries = useQueryParams()

  const isFromAd = isFromPaidAd(queries)
  useEffect(() => {
    if (isFromAd) {
      history.push({ ...history.location })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isFromAd) {
      const handlePopState = async(event: PopStateEvent) => {
        event.preventDefault()
        if (!event.state?.showModal &&
          (!!offerPageSecondChanceModalVariant || !!searchPageSecondChanceModalVariant)
        ) {
          setIsModalVisible(true)
          let result
          if (pageType === 'offer' && seedOffer) {
            result = await showModal<'stay' | 'goBack'>(
              <SecondChanceModal
                seedOffer={seedOffer}
                pageType={PageType.Offer}
             />,
            )
          } else {
            result = await showModal<'stay' | 'goBack'>(
              <SecondChanceModal
                pageType={PageType.Search}
              />,
            )
          }

          if (result === 'goBack') {
            history.goBack()
          }
        } else {
          history.goBack()
        }
      }
      window.addEventListener('popstate', handlePopState)

      return () => {
        window.removeEventListener('popstate', handlePopState)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerPageSecondChanceModalVariant, searchPageSecondChanceModalVariant])

  return { isModalVisible }
}

export default useSecondChanceModal
